import { Mail, RefreshCw, Copy, Inbox, ChevronRight, Send, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { decodeMimeWords, senderLabel } from "@/lib/mime";

export default function EmailInboxView({ email, messages, isLoading, onRefresh, onMessageClick, formatTime, copyToClipboard }) {
  return (
    <div>
      {/* Email Info Bar */}
      <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
            <Mail className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <p className="text-white font-mono text-sm">{email.full_address}</p>
            <p className="text-xs text-zinc-500">
              {email.status === "permanent" ? "Email permanent" : `Expire dans ${email.remaining_seconds > 0 ? Math.floor(email.remaining_seconds / 60) + " min" : "Expiré"}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => copyToClipboard(email.full_address)} variant="outline" size="sm" className="border-zinc-700 text-xs">
            <Copy className="w-3 h-3 mr-1" />
            Copier
          </Button>
          <Button onClick={onRefresh} variant="outline" size="sm" className="border-zinc-700 text-xs hover:text-amber-400 hover:border-amber-500/40 hover:bg-amber-500/10 group/refresh" disabled={isLoading}>
            <RefreshCw className={`w-3 h-3 mr-1 transition-transform duration-500 ${isLoading ? "animate-spin" : "group-hover/refresh:rotate-[360deg]"}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Messages List */}
      <div className="border border-zinc-800 rounded-lg bg-zinc-900/30">
        <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4 text-cyan-400" />
            <span className="font-mono text-sm text-zinc-400">Messages</span>
            <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 text-xs">
              {messages.length}
            </Badge>
          </div>
        </div>

        <ScrollArea className="h-[400px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-zinc-500 animate-spin" />
            </div>
          ) : messages.length > 0 ? (
            <div className="divide-y divide-zinc-800/50">
              {messages.map((message) => {
                const isSent = message.direction === "sent";
                return (
                <div
                  key={message.id}
                  onClick={() => onMessageClick(message.id)}
                  className={`p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors border-l-2 ${
                    isSent
                      ? "border-emerald-500/50 bg-emerald-500/5"
                      : message.is_read
                        ? "border-transparent"
                        : "border-cyan-500 bg-cyan-500/5"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {isSent ? (
                          <Send className="w-3 h-3 text-emerald-400 shrink-0" />
                        ) : !message.is_read ? (
                          <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                        ) : null}
                        <span className={`font-mono text-sm truncate ${isSent ? "text-emerald-400" : "text-white"}`}>
                          {isSent ? `→ ${message.recipient || message.sender}` : senderLabel(message)}
                        </span>
                        {isSent && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-mono shrink-0">Envoyé</span>
                        )}
                      </div>
                      <p className="text-zinc-300 truncate font-medium text-sm flex items-center gap-1">
                        {message.attachments?.length > 0 && <Paperclip className="w-3 h-3 text-amber-400/60 shrink-0" />}
                        {decodeMimeWords(message.subject)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-500">
                      <span className="font-mono text-xs">
                        {formatTime(message.received_at)}
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-zinc-600">
              <Inbox className="w-12 h-12 mb-4 opacity-50" />
              <p className="font-mono text-sm">Aucun message</p>
              <p className="text-xs mt-1">Les messages reçus apparaîtront ici</p>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
